import { useEffect, useMemo, useState } from 'react';

import { AutocompleteInputChangeReason, Typography } from '@mui/material';
import { Stack, SxProps } from '@mui/system';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import DevicesService from '~features/devices/services/devicesService';
import { BaseReference } from '~interfaces';
import DeviceModel from '~interfaces/deviceModel';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import AsyncAutocomplete from './asyncAutocomplete';

const service = getService(DevicesService);

interface RolesDropdownProps {
	onValueChanged: (newValue?: DeviceModel) => void;
	label?: string;
	value?: string | DeviceModel;
	sx?: SxProps;
}

const DeviceModelAutocomplete = ({ label, value, ...props }: RolesDropdownProps) => {
	const { t } = useTranslation();
	const { data } = useSWR(['deviceModels'], ([_]) => service.getDeviceModels(), {
		keepPreviousData: true,
		revalidateFirstPage: false,
	});

	const [filteredDeviceModels, setFilteredDeviceModels] = useState([] as DeviceModel[]);
	const deviceModels = useMemo(() => data ?? [], [data]);

	useEffect(() => {
		// This is an okay use-case when data is fetched from API and depends on user input.
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setFilteredDeviceModels(deviceModels);
	}, [deviceModels]);

	// Input handling
	const handleInputChange = (
		_event: React.SyntheticEvent,
		value: string,
		_reason: AutocompleteInputChangeReason
	) => {
		if (value.length <= 0) {
			setFilteredDeviceModels(deviceModels);
		} else {
			setFilteredDeviceModels(
				deviceModels.filter((deviceModel) =>
					deviceModel.name.toLowerCase().includes(value.toLowerCase())
				)
			);
		}
	};

	const selectedDeviceModel = useMemo(() => {
		const selectedName = (value as DeviceModel)?.name ?? value;
		if (selectedName == null || selectedName === '') {
			return null;
		}

		return deviceModels.find((deviceModel) => deviceModel.name == selectedName) ?? null;
	}, [deviceModels, value]);

	const handleOptionSelected = (value: BaseReference | undefined) => {
		props.onValueChanged(deviceModels.find((deviceModel) => deviceModel.id == value?.id));
	};

	return (
		<Stack sx={{ gap: Dimens.spacingXs }}>
			{label && <Typography variant='labelSm'>{label}</Typography>}
			<AsyncAutocomplete
				value={selectedDeviceModel}
				placeholder={t('device.fields.model', { ns: 'entities' })}
				noOptionsText={t('noResults', { ns: 'system' })}
				options={filteredDeviceModels}
				onInputChange={handleInputChange}
				onOptionSelected={handleOptionSelected}
				debounce={false}
				enableInfiniteScroll={false}
				sx={{
					width: 200,
					...props.sx,
				}}
			/>
		</Stack>
	);
};

export default DeviceModelAutocomplete;
